import React, { useEffect, useState } from "react";
import { api, fmtCurrency, fmtDate, fmtDay, userRoles } from "@/lib/api";
import { useI18n } from "@/contexts/I18nContext";
import { useAuth } from "@/contexts/AuthContext";
import { StatusBadge } from "@/components/StatusBadge";
import { Link } from "react-router-dom";
import { BarChart, Bar, XAxis, YAxis, ResponsiveContainer, Tooltip, CartesianGrid } from "recharts";
import { TrendingUp, Calendar, Users, DollarSign, ArrowUpRight, Sparkles } from "lucide-react";

export default function DashboardPage() {
  const { user } = useAuth();
  const { t } = useI18n();
  const [orders, setOrders] = useState([]);
  const [stats, setStats] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    (async () => {
      try {
        const [o, s] = await Promise.all([
          api.get("/orders"),
          user && !userRoles(user).every((r) => r === "customer") ? api.get("/reports/sales").catch(() => ({ data: null })) : Promise.resolve({ data: null }),
        ]);
        setOrders(o.data || []);
        setStats(s.data);
      } finally {
        setLoading(false);
      }
    })();
  }, [user?.role]);

  const upcoming = orders.filter((o) => o.status !== "completed").slice(0, 5);
  const chartData = (stats?.by_month || []).map((x) => ({ month: x.month, amount: x.amount }));

  return (
    <div className="p-6 md:p-10 max-w-7xl mx-auto" data-testid="dashboard-page">
      {/* Hero */}
      <div className="flex items-end justify-between mb-10 flex-wrap gap-4 fade-up">
        <div>
          <div className="label-eyebrow">Tonight at HNC</div>
          <h1 className="font-display text-4xl md:text-5xl mt-2">Good evening, {user?.name?.split(" ")[0]}.</h1>
          <p className="text-zinc-500 mt-2 text-sm">A bird's eye view of every plate, every guest, every moment.</p>
        </div>
        <Link to="/orders" data-testid="quick-orders" className="px-5 py-2.5 rounded-sm border border-[#2a2a2a] hover:border-[#D4AF37] hover:text-[#D4AF37] transition-colors flex items-center gap-2 text-sm">
          View all orders <ArrowUpRight className="w-4 h-4" />
        </Link>
      </div>

      {/* Stat cards */}
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4 mb-8">
        <StatCard icon={Calendar} label="Upcoming Events" value={stats?.upcoming_count ?? orders.filter((o) => o.status !== "completed").length} tint="#D4AF37" />
        <StatCard icon={DollarSign} label="Total Revenue" value={fmtCurrency(stats?.total_revenue)} tint="#10B981" />
        <StatCard icon={TrendingUp} label="Collected" value={fmtCurrency(stats?.total_collected)} tint="#3B82F6" />
        <StatCard icon={Users} label="Outstanding" value={fmtCurrency(stats?.outstanding)} tint="#F59E0B" />
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-3 gap-4">
        {/* Chart */}
        <div className="card-lux p-6 lg:col-span-2">
          <div className="flex items-center justify-between mb-4">
            <div>
              <div className="label-eyebrow">Sales by Month</div>
              <h3 className="font-display text-xl mt-1">Revenue overview</h3>
            </div>
            <Sparkles className="w-4 h-4 text-[#D4AF37]" />
          </div>
          <div style={{ height: 256, minHeight: 256 }}>
            {chartData.length === 0 ? (
              <div className="h-full flex items-center justify-center text-zinc-600 text-sm">No data yet</div>
            ) : (
              <ResponsiveContainer width="100%" height={256}>
                <BarChart data={chartData}>
                  <CartesianGrid strokeDasharray="2 4" stroke="#1a1a1a" vertical={false} />
                  <XAxis dataKey="month" stroke="#52525b" fontSize={11} tickLine={false} axisLine={false} />
                  <YAxis stroke="#52525b" fontSize={11} tickLine={false} axisLine={false} />
                  <Tooltip
                    contentStyle={{ background: "#0a0a0a", border: "1px solid #2a2a2a", borderRadius: 4, fontSize: 12 }}
                    cursor={{ fill: "rgba(212,175,55,0.05)" }}
                  />
                  <Bar dataKey="amount" fill="#D4AF37" radius={[4, 4, 0, 0]} />
                </BarChart>
              </ResponsiveContainer>
            )}
          </div>
        </div>

        {/* By status */}
        <div className="card-lux p-6">
          <div className="label-eyebrow">By Status</div>
          <h3 className="font-display text-xl mt-1 mb-4">Order pipeline</h3>
          <div className="space-y-3">
            {Object.entries(stats?.by_status || {}).map(([s, n]) => (
              <div key={s} className="flex items-center justify-between" data-testid={`pipeline-${s}`}>
                <StatusBadge status={s} />
                <div className="font-display text-lg">{n}</div>
              </div>
            ))}
            {Object.keys(stats?.by_status || {}).length === 0 && (
              <div className="text-zinc-600 text-sm">No orders yet</div>
            )}
          </div>
        </div>
      </div>

      {/* Upcoming events */}
      <div className="card-lux p-6 mt-4">
        <div className="flex items-center justify-between mb-4">
          <div>
            <div className="label-eyebrow">{t("common.upcoming")}</div>
            <h3 className="font-display text-xl mt-1">Next on the calendar</h3>
          </div>
        </div>
        <div className="divide-y divide-[#1a1a1a]">
          {loading && <div className="py-10 text-center text-zinc-600 text-sm">Loading…</div>}
          {!loading && upcoming.length === 0 && (
            <div className="py-10 text-center text-zinc-600 text-sm">No upcoming events</div>
          )}
          {upcoming.map((o) => (
            <Link to={`/orders/${o.id}`} key={o.id} className="flex items-center justify-between py-4 hover:bg-[#0c0c0c] px-2 -mx-2 rounded-sm transition-colors" data-testid={`upcoming-${o.id}`}>
              <div className="flex-1 min-w-0">
                <div className="font-display text-lg truncate">{o.customer_name}</div>
                <div className="text-xs text-zinc-500 mt-0.5 truncate">{o.event_location} · {o.pax} {t("common.pax")}</div>
              </div>
              <div className="text-right hidden sm:block px-4">
                <div className="text-sm text-zinc-300">{fmtDay(o.event_date)}</div>
                <div className="text-xs text-zinc-500 mt-0.5">{fmtCurrency(o.total_amount)}</div>
              </div>
              <StatusBadge status={o.status} />
            </Link>
          ))}
        </div>
      </div>
    </div>
  );
}

function StatCard({ icon: Icon, label, value, tint }) {
  return (
    <div className="card-lux p-5 fade-up">
      <div className="flex items-center justify-between">
        <div className="label-eyebrow">{label}</div>
        <div className="w-7 h-7 rounded-sm flex items-center justify-center" style={{ background: `${tint}15`, color: tint }}>
          <Icon className="w-4 h-4" />
        </div>
      </div>
      <div className="font-display text-3xl mt-3">{value}</div>
    </div>
  );
}
